import { Component, computed, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PcMediaWatcherService } from 'app/shared/services/media-watcher';
import { PcNavigationItem } from 'app/shared/components/navigation';
import { ModuleService } from 'app/core/services/module/module.service';
import { User } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { AuthService } from 'app/core/auth/auth.service';
import { MethodCustom } from 'app/shared/utils/method';
import { PRIMECONTROL_BUTTONS } from 'app/shared/buttons/buttons';
import { SvgIcon } from 'app/shared/svg-icon/svg-icon';
import { LoadingSpinnerComponent } from 'app/shared/organisms/loading-spinner/loading-spinner';
import { TranslationsComponent } from 'app/shared/organisms/translations/translations';
import { UserComponent } from 'app/layout/common/user/user.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { SideBar } from 'app/shared/side-bar/side-bar';
import { SideBarItem } from 'app/shared/side-bar/sidebar-item';
import {
  CONTACTS_TABS,
  EQUIPMENT_TABS,
  MARKETING_TABS,
  OFFERS_TABS,
  QUOTES_TABS,
  SALES_ORDERS_TABS,
  TEAM_TABS_LINKED,
} from 'app/shared/constants/page-tab-groups';

@Component({
  selector: 'classy-layout',
  templateUrl: './classy.component.html',
  styleUrls: ['./classy.component.css'],
  imports: [
    CommonModule,
    RouterModule,
    PRIMECONTROL_BUTTONS,
    SvgIcon,
    TranslateModule,
    LoadingSpinnerComponent,
    TranslationsComponent,
    NotificationsComponent,
    UserComponent,
    SideBar,
  ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: WritableSignal<boolean> = signal(false);
  user: User;
  logo: string;

  sideBarItems: WritableSignal<SideBarItem[]> = signal<SideBarItem[]>([]);
  /** Sidebar header label — switches with the navigation context. */
  sidebarTitle: WritableSignal<string> = signal('MENU');
  mobileMenuOpen: WritableSignal<boolean> = signal(false);
  sidebarPinned: WritableSignal<boolean> = signal(
    localStorage.getItem('pc-sidebar-pinned') === 'true'
  );

  /** Offset of the content area: 0 on mobile, floating rail (60px + gaps) collapsed, full width pinned. */
  contentMarginLeft = computed(() => {
    if (this.isScreenSmall()) return '0px';
    return this.sidebarPinned() ? '17rem' : '4.75rem';
  });

  private _modules: any[] | null = null;
  /** Flattened instance-module nav, cached so context switches don't rebuild it. */
  private _instanceItems: SideBarItem[] = [];
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _userService: UserService,
    private _pcMediaWatcherService: PcMediaWatcherService,
    private _moduleService: ModuleService,
    private _translateService: TranslateService,
    private _router: Router,
    private _authService: AuthService
  ) {
    const preference = sessionStorage.getItem('primecontrol_preference')
      ? sessionStorage.getItem('primecontrol_preference')
      : localStorage.getItem('primecontrol_preference');
    const { horizontal_logo, square_logo } = JSON.parse(preference);
    this.logo = horizontal_logo ? horizontal_logo : square_logo;
  }

  /** Name for the phone menu's header; falls back to the e-mail's local part,
   *  as the navbar's user menu does. */
  get userName(): string {
    const name = [this.user?.firstName, this.user?.lastName].filter(Boolean).join(' ');
    return name || this.user?.email?.split('@')[0] || '';
  }

  /** Cerrar sesión from the phone menu — the same flow as the user menu's. */
  signOut(): void {
    this.mobileMenuOpen.set(false);
    this._authService.signOut().subscribe(() => {
      this._router.navigate(['auth/sign-in']);
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.user = JSON.parse(this._userService.user$);

    // Build the sidebar items from the user's modules
    this._moduleService.modulesForNavigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((modules: any[]) => {
        if (modules) {
          this._modules = modules;
          this._buildSideBarItems();
        }
      });

    // Rebuild on language change (item titles are translated at build time)
    this._translateService.onLangChange.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      this._buildSideBarItems();
      this._refreshSideBar();
    });

    // Swap the sidebar set when entering/leaving the profile (organization)
    // area — like brandbot's per-context layouts, but driven off the route.
    this._router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => this._refreshSideBar());
    this._refreshSideBar();

    // Subscribe to media changes
    this._pcMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        this.isScreenSmall.set(!matchingAliases.includes('md'));
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  private _buildSideBarItems(): void {
    if (!this._modules) {
      return;
    }
    MethodCustom.buildMenuStructure(this._modules, this._translateService).subscribe(
      (navigation: PcNavigationItem[]) => {
        this._instanceItems = this._flatten(navigation);
        this._refreshSideBar();
      }
    );
  }

  /** True while the user is in the profile / organization area. */
  private _isProfileContext(): boolean {
    return this._router.url.startsWith('/profile');
  }

  /** True while the user is inside a workspace's settings area. */
  private _isWorkspaceContext(): boolean {
    const url = this._router.url;
    return /^\/settings\/instances\/[^/]+/.test(url) || url.startsWith('/settings/modules');
  }

  /**
   * Workspace navigation — shown while under /settings/instances/:id, mirrors
   * brandbot's workspaces context sidebar (Overview / Users / Roles / …).
   * Items target the workspace's own child routes so the active highlight
   * tracks the page the user is on.
   */
  private _workspaceItems(): SideBarItem[] {
    const t = (key: string): string => this._translateService.instant(key);
    const match = this._router.url.match(/^\/settings\/instances\/([^/?#]+)/);
    // When viewing a workspace-level page that isn't nested under the id
    // (e.g. /settings/modules), fall back to the active instance from storage
    // so each item still points at the right workspace.
    const id =
      match?.[1] ??
      sessionStorage.getItem('primecontrol_idInstance') ??
      localStorage.getItem('primecontrol_idInstance') ??
      '';
    const base = `/settings/instances/${id}`;
    return [
      {
        type: 'item',
        title: t('HOME'),
        routerLink: '/home',
        ref: 'home',
        exact: true,
        icon: 'mat_outline:home',
      },
      { type: 'separator', title: t('WORKSPACE') },
      // The former Overview / Preferences / Email / Lead-webhook / Business /
      // Availability / Sequences / Blacklist / Module-visibility items are now
      // a single consolidated Workspace settings screen with an in-page rail.
      // First in the group: it is where the instance opens (the :idInstance
      // route redirects to it), so the rail lists it where the user lands.
      {
        type: 'item',
        title: t('WORKSPACE_SETTINGS'),
        routerLink: `${base}/settings`,
        ref: `${base}/settings`,
        icon: 'heroicons_outline:cog',
      },
      {
        type: 'item',
        title: t('ROLES'),
        routerLink: `${base}/roles`,
        ref: `${base}/roles`,
        icon: 'heroicons_outline:shield-check',
      },
      {
        type: 'item',
        title: t('USERS'),
        routerLink: `${base}/users`,
        ref: `${base}/users`,
        icon: 'heroicons_outline:users',
      },
      {
        type: 'item',
        title: t('SECRETS_AND_VARIABLES'),
        routerLink: `${base}/secrets-and-variables`,
        ref: `${base}/secrets-and-variables`,
        icon: 'heroicons_outline:lock-closed',
      },
      {
        type: 'item',
        title: t('STORAGE'),
        routerLink: `${base}/storage`,
        ref: `${base}/storage`,
        icon: 'heroicons_outline:server',
      },
      {
        type: 'item',
        title: t('MODULES'),
        routerLink: '/settings/modules',
        ref: '/settings/modules',
        icon: 'mat_outline:list_alt',
      },
    ];
  }

  /**
   * Profile/organization navigation — a distinct set shown while under
   * /profile, instead of the instance modules (mirrors brandbot's separate
   * profile layout). Titles are pre-translated because the side-bar renders
   * item.title verbatim (instance items are translated at build time).
   */
  private _profileItems(): SideBarItem[] {
    const t = (key: string): string => this._translateService.instant(key);
    return [
      {
        type: 'item',
        title: t('HOME'),
        routerLink: '/home',
        ref: 'home',
        exact: true,
        icon: 'mat_outline:home',
      },
      { type: 'separator', title: t('PROFILE') },
      {
        type: 'item',
        title: t('preferences'),
        routerLink: '/profile/preferences',
        ref: 'preferences',
        icon: 'heroicons_outline:cog',
      },
      {
        type: 'item',
        title: t('ORGANIZATIONS'),
        routerLink: '/profile/organizations',
        ref: 'organizations',
        icon: 'mat_outline:business',
      },
      {
        type: 'item',
        title: t('SECURITY'),
        routerLink: '/profile/security',
        ref: 'security',
        icon: 'mat_outline:security',
      },
    ];
  }

  /** Pick the sidebar set + header label for the current route context. */
  private _refreshSideBar(): void {
    if (this._isWorkspaceContext()) {
      this.sideBarItems.set(this._workspaceItems());
      this.sidebarTitle.set('WORKSPACE');
      return;
    }
    if (this._isProfileContext()) {
      this.sideBarItems.set(this._profileItems());
      this.sidebarTitle.set('PROFILE');
      return;
    }
    this.sideBarItems.set(this._instanceItems);
    this.sidebarTitle.set('MENU');
  }

  /**
   * Instance-sidebar links that have been re-homed to the workspace context.
   * Dropping them from the global navigation keeps the regular sidebar
   * focused on day-to-day work; workspace-level config lives behind the
   * Workspace settings button on each card.
   */
  private static readonly _workspaceOnlyLinks = new Set(['/settings/modules', '/settings/roles']);

  /** Flatten the grouped pc navigation tree into the side-bar's flat item model. */
  private _flatten(navigation: PcNavigationItem[]): SideBarItem[] {
    const items: SideBarItem[] = [];

    for (const node of navigation) {
      if (node.type === 'group' && node.children?.length) {
        const childItems = node.children
          .filter((c) => !ClassyLayoutComponent._workspaceOnlyLinks.has(c.link))
          .map((c) => this._toSideBarItem(c));
        if (!childItems.length) continue;
        items.push({ type: 'separator', title: node.title || '' });
        items.push(...childItems);
      } else if (node.type === 'divider') {
        items.push({ type: 'separator', title: '' });
      } else {
        if (ClassyLayoutComponent._workspaceOnlyLinks.has(node.link)) continue;
        items.push(this._toSideBarItem(node));
      }
    }

    return items;
  }

  private _toSideBarItem(node: PcNavigationItem): SideBarItem {
    return {
      type: 'item',
      title: node.title || '',
      routerLink: node.link,
      // A nav entry stands for a whole tab group, so it stays lit on any of the
      // group's pages — otherwise opening Customers or Customer groups from
      // Directory left the sidebar with nothing selected, as if the user had
      // wandered out of the module they were still inside.
      ref: SIBLING_REFS.get(node.link) ?? node.link,
      exact: node.link === '/home',
      icon: node.icon,
    };
  }
}

/**
 * Every route that should keep a nav entry lit, keyed by the link that entry
 * points at, joined the way `SideBarItem.ref` expects.
 *
 * Derived from the tab groups rather than restated here: those constants
 * already declare which sibling pages are one module — their own docstring says
 * the sidebar keeps one entry per group — so a page that joins a group starts
 * highlighting its nav entry with no second edit, and the two cannot drift.
 */
const SIBLING_REFS: ReadonlyMap<string, string> = new Map(
  [
    QUOTES_TABS,
    SALES_ORDERS_TABS,
    OFFERS_TABS,
    EQUIPMENT_TABS,
    MARKETING_TABS,
    CONTACTS_TABS,
    TEAM_TABS_LINKED,
  ].flatMap((group) => {
    // Deduped: a group can point two tabs at one route (Collaborators and
    // Departments share a page and differ only by query param).
    const links = [...new Set(group.map((tab) => tab.link).filter(Boolean))];
    return links.map((link) => [link, links.join(',')] as const);
  })
);
